// components/TableComponentAptMetrix.jsx
import React from 'react';
import { downloadDataAsCSV } from '../util/helper';
import Spin from './Spin';
import { useNavigate } from 'react-router-dom';
import SubwayInfoTooltip from './SubwayInfoTooltip';
import { useSortContext } from '../context/aptmetrix/SortContext';
import { useSubwayTooltip } from '../hooks/useSubwayTooltip';
import { useTablePreprocess } from './table/useTablePreprocess';
import { useTableSort } from './table/useTableSort';
import { renderCell } from './table/cellRenderers';
import {
  APTMETRIX_SORTABLE_COLUMNS,
  APTMETRIX_COLUMN_NAME_MAPPING,
  buildFinalColumnOrder,
} from './table/columnConfig';

function TableComponentAptMetrix({ data, columnOrder = [], onSaleClick, onBookmarkClick, isLoading, error }) {
  const navigate = useNavigate();
  const { sortConfig, setSortConfig } = useSortContext();
  const { preprocessedData, isDataFullyLoaded } = useTablePreprocess(data);
  const { sortedData, isSortingAvailable, requestSort, getSortIndicator, resetSort } =
    useTableSort(preprocessedData, isDataFullyLoaded, sortConfig, setSortConfig, APTMETRIX_SORTABLE_COLUMNS);

  const {
    showSubwayTooltip, selectedSubwayInfo, tooltipRef,
    handleShowSubwayInfo, handleCloseSubwayTooltip,
  } = useSubwayTooltip();

  const finalColumnOrder = buildFinalColumnOrder(columnOrder, { includeBookmark: !!onBookmarkClick });
  const handlers = { navigate, onSaleClick, handleShowSubwayInfo, onBookmarkClick };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spin size={32} /><span className="ml-3 text-gray-700 text-xl md:text-3xl">데이터를 불러오는 중입니다...</span>
      </div>
    );
  }

  return (
    <div className='w-full min-h-[50vh]'>
      <div className='flex justify-start md:justify-end items-center mt-2'>
        <button
          className="mb-1 mr-2 p-1 text-xs md:text-sm border border-gray-100 bg-gray-100 hover:bg-gray-200 text-green-600 rounded"
          onClick={resetSort}
          disabled={!isSortingAvailable || !sortConfig.key}
          title="정렬 초기화"
        >
          정렬 초기화
        </button>
        <button
          className="hidden lg:block mb-1 p-1 border text-xs md:text-sm border-gray-100 bg-orange-100 hover:bg-orange-200 text-black rounded underline"
          onClick={() => downloadDataAsCSV(data, finalColumnOrder)}
          disabled={!data}
        >
          Download CSV
        </button>
        <div className="ml-2 mb-2 p-1 lg:hidden text-xs md:text-sm text-gray-500">➡️ 왼쪽으로 스크롤하세요</div>
      </div>
      <div className="max-h-[80vh] overflow-y-auto">
        <table className='border border-collapse border-gray-100 table-auto w-full'>
          <thead className="sticky top-[-1px] z-10">
            <tr className="h-12">
              {finalColumnOrder.map((column, index) => (
                <th
                  key={index}
                  className={`border border-gray-200 text-left bg-[#fffdec] p-2
                    ${column === 'apartment_name' ? 'min-w-[150px] w-[150px]' : ''}
                    ${column === 'subdistrict_name' ? 'min-w-[50px]' : ''}
                    ${column === 'type' ? 'min-w-[50px]' : ''}
                    ${APTMETRIX_SORTABLE_COLUMNS.includes(column) && isSortingAvailable ? 'cursor-pointer hover:bg-[#f5f0d0]' : ''}`}
                  onClick={() => APTMETRIX_SORTABLE_COLUMNS.includes(column) && isSortingAvailable ? requestSort(column) : null}
                  title={APTMETRIX_SORTABLE_COLUMNS.includes(column) && isSortingAvailable ? '클릭하여 정렬하기' : ''}
                >
                  <div className={`text-xs md:text-sm ${column === 'apartment_name' ? 'break-words' : 'whitespace-nowrap'}
                    overflow-hidden text-ellipsis
                    ${APTMETRIX_SORTABLE_COLUMNS.includes(column) && isSortingAvailable ? 'text-green-700' : ''}`}>
                    {APTMETRIX_COLUMN_NAME_MAPPING[column] || column}
                    {getSortIndicator(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(error || !sortedData || sortedData.length === 0) ? (
              <tr>
                <td colSpan={finalColumnOrder.length} className="border border-gray-100 text-center p-8">
                  <div className="flex flex-col items-center justify-center">
                    <p className="text-lg md:text-xl text-gray-600 mb-2">
                      {error ? '데이터를 불러오는 중 오류가 발생했습니다.' : '검색 결과가 없습니다.'}
                    </p>
                    <p className="text-sm md:text-base text-gray-500">
                      {error || !data ? '다시 검색해주세요.' : '필터 조건을 확인해주세요.'}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row, rowIndex) => (
                <tr key={rowIndex} className="h-9">
                  {finalColumnOrder.map((column, colIndex) =>
                    renderCell(column, row, colIndex, handlers)
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showSubwayTooltip && selectedSubwayInfo && (
        <div ref={tooltipRef}>
          <SubwayInfoTooltip
            subwayInfo={selectedSubwayInfo}
            onClose={handleCloseSubwayTooltip}
          />
        </div>
      )}
    </div>
  );
}

export default TableComponentAptMetrix;
